import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { auth as authApi } from '../utils/api';

// Reset password self-service, conferma (Fetta 5b). Il link (self-service o
// admin) porta uid+token nel FRAGMENT (#), non in query: così non finiscono nei
// log né come Referer (FE-001). Qui si legge il fragment e si imposta la nuova
// password.
function leggiHash() {
  const raw = (window.location.hash || '').replace(/^#/, '');
  const params = new URLSearchParams(raw);
  return { uid: params.get('uid') || '', token: params.get('token') || '' };
}

const MSG_ERRORE = {
  token_non_valido: 'Il link non è valido o è scaduto. Richiedine uno nuovo dalla pagina di accesso.',
  password_debole: 'La password è troppo debole. Usa almeno 8 caratteri con lettere e numeri.',
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [{ uid, token }] = useState(leggiHash);
  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [fatto, setFatto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const linkValido = Boolean(uid && token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== conferma) {
      setError('Le due password non coincidono.');
      return;
    }
    setLoading(true);
    try {
      await authApi.passwordResetConfirm({ uid, token, nuova_password: password });
      setFatto(true);
    } catch (err) {
      const codice = err.response?.data?.codice;
      setError(
        MSG_ERRORE[codice] ||
        err.response?.data?.errore ||
        'Non siamo riusciti ad aggiornare la password. Riprova.'
      );
    } finally {
      setLoading(false);
    }
  };

  let corpo;
  if (fatto) {
    corpo = (
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6, marginBottom: 20 }}>
          Password aggiornata. Ora puoi accedere con la nuova password.
        </p>
        <button onClick={() => navigate('/auth/login')} className="gradient-brand" style={btnPrimario}>
          Vai all'accesso
        </button>
      </div>
    );
  } else if (!linkValido) {
    corpo = (
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6, marginBottom: 20 }}>
          Questo link di reset non è valido. Richiedine uno nuovo dalla pagina di accesso.
        </p>
        <button onClick={() => navigate('/auth/password-dimenticata')} className="gradient-brand" style={btnPrimario}>
          Richiedi un nuovo link
        </button>
      </div>
    );
  } else {
    corpo = (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>Nuova password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'} value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required style={inputStyle}
            />
            <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {showPw ? <Eye size={16} strokeWidth={2} color="var(--ds-muted)" /> : <EyeOff size={16} strokeWidth={2} color="var(--ds-muted)" />}
            </button>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Ripeti la nuova password</label>
          <input
            type={showPw ? 'text' : 'password'} value={conferma}
            onChange={(e) => setConferma(e.target.value)}
            placeholder="••••••••" required style={inputStyle}
          />
        </div>
        {error && <div style={erroreStyle}>{error}</div>}
        <button type="submit" disabled={loading} className="gradient-brand" style={{ ...btnPrimario, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Aggiornamento…' : 'Imposta la nuova password'}
        </button>
      </form>
    );
  }

  return <Guscio titolo="Reimposta la password">{corpo}</Guscio>;
}

const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--ds-text)', display: 'block', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
  background: 'var(--chip)', fontSize: 12, color: 'var(--ds-text)', boxSizing: 'border-box',
};
const btnPrimario = {
  width: '100%', padding: '11px 0', borderRadius: 'var(--radius-base)', border: 'none',
  cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'white', boxShadow: '0 2px 10px rgba(35,38,47,0.06)',
};
const erroreStyle = {
  fontSize: 11, color: 'var(--ds-danger)', background: 'color-mix(in srgb, var(--ds-danger) 8%, transparent)',
  borderRadius: 'var(--radius-base)', padding: '8px 12px',
};

function Guscio({ titolo, children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--chip)', padding: '32px 0' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
          <div className="gradient-brand" style={{ width: 52, height: 52, borderRadius: 'var(--radius-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
            <span style={{ color: 'white', fontWeight: 500, fontSize: 22, fontStyle: 'italic' }}>m</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>mAIstro</div>
        </div>
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: 28, boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginTop: 0, marginBottom: 16, fontFamily: 'var(--font-display)' }}>{titolo}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}
